import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Play, MessageCircle, Zap, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

const Hero = () => {
  const { language, t } = useLanguage();
  const isRTL = language === 'ar';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const scrollToPackages = () => {
    const element = document.querySelector('#packages');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center hero-pattern overflow-hidden pt-20" data-testid="hero-section">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating shapes */}
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 start-10 w-20 h-20 rounded-2xl bg-emerald-100/50 backdrop-blur-sm"
        />
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute top-1/3 end-20 w-16 h-16 rounded-full bg-emerald-200/40"
        />
        <motion.div
          animate={{ y: [0, -15, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-1/4 start-1/4 w-24 h-24 rounded-3xl bg-emerald-50/60 backdrop-blur-sm"
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-start"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-6"
              data-testid="hero-badge"
            >
              <Zap className="w-4 h-4" />
              {t('hero_badge')}
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
              data-testid="hero-title"
            >
              {t('hero_title')}{' '}
              <span className="gradient-text">{t('hero_title_highlight')}</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0"
              data-testid="hero-subtitle"
            >
              {t('hero_subtitle')}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-4"
            >
              <Button
                onClick={scrollToPackages}
                className="btn-primary flex items-center gap-2"
                data-testid="hero-cta"
              >
                {t('hero_cta')}
                <ArrowIcon className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                className="btn-secondary flex items-center gap-2"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-4 h-4" />
                {t('hero_cta_secondary')}
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-center justify-center lg:justify-start gap-8 mt-12"
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">10K+</div>
                <div className="text-sm text-slate-500">{isRTL ? 'عميل سعيد' : 'Happy Clients'}</div>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">5M+</div>
                <div className="text-sm text-slate-500">{isRTL ? 'رسالة مرسلة' : 'Messages Sent'}</div>
              </div>
              <div className="w-px h-12 bg-slate-200" />
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900">99.9%</div>
                <div className="text-sm text-slate-500">{isRTL ? 'وقت التشغيل' : 'Uptime'}</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            {/* Phone mockup */}
            <div className="relative mx-auto w-72 sm:w-80">
              {/* Phone frame */}
              <div className="relative bg-slate-900 rounded-[3rem] p-3 shadow-2xl whatsapp-glow">
                {/* Screen */}
                <div className="bg-emerald-50 rounded-[2.5rem] overflow-hidden">
                  {/* WhatsApp header */}
                  <div className="bg-emerald-600 px-4 py-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white font-semibold text-sm">WatsFlow API</div>
                      <div className="text-emerald-100 text-xs">{isRTL ? 'متصل' : 'Online'}</div>
                    </div>
                  </div>
                  
                  {/* Chat area */}
                  <div className="p-4 space-y-3 min-h-[280px] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMxMGI5ODEiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PHBhdGggZD0iTTM2IDM0djItSDI0di0yaDEyek0zNiAzMHYySDI0di0yaDEyek0zNiAyNnYySDI0di0yaDEyeiIvPjwvZz48L2c+PC9zdmc+')]">
                    {/* Received message */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white rounded-2xl rounded-tl-none px-4 py-2 shadow-sm max-w-[80%]">
                        <p className="text-sm text-slate-700">
                          {isRTL ? 'مرحباً! كيف يمكنني مساعدتك؟' : 'Hello! How can I help you?'}
                        </p>
                        <span className="text-xs text-slate-400">10:30 AM</span>
                      </div>
                    </motion.div>
                    
                    {/* Sent message */}
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1 }}
                      className="flex justify-end"
                    >
                      <div className="bg-emerald-500 rounded-2xl rounded-tr-none px-4 py-2 shadow-sm max-w-[80%]">
                        <p className="text-sm text-white">
                          {isRTL ? 'أريد ربط حسابي بالواتساب' : 'I want to connect my WhatsApp'}
                        </p>
                        <span className="text-xs text-emerald-100">10:31 AM</span>
                      </div>
                    </motion.div>
                    
                    {/* QR Code message */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.2 }}
                      className="flex justify-start"
                    >
                      <div className="bg-white rounded-2xl rounded-tl-none px-4 py-3 shadow-sm">
                        <p className="text-sm text-slate-700 mb-2">
                          {isRTL ? 'امسح هذا الكود للربط:' : 'Scan this code to connect:'}
                        </p>
                        <div className="w-24 h-24 bg-slate-100 rounded-lg p-2 relative overflow-hidden">
                          <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMSAyMSI+PHBhdGggZD0iTTEgMWg3djdIMXptMiAydjNoM1Yzem0xMi03aDd2N2gtN3ptMiAydjNoM1Yzek0xIDEzaDd2N0gxem0yIDJ2M2gzdi0zem0xMS01aDN2M2gtM3ptMCAzaDN2M2gtM3ptLTMgMGgzdjNoLTN6bTAgM2gzdjNoLTN6bTMgMGgzdjNoLTN6TTQgNGgxdjFINHptMCAxMGgxdjFINHptMTAtMTBoMXYxaC0xeiIvPjwvc3ZnPg==')] bg-contain" />
                          <div className="absolute inset-0 bg-gradient-to-b from-emerald-400/20 to-transparent qr-scan-line" />
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -start-8 top-1/4 bg-white rounded-2xl p-3 shadow-lg flex items-center gap-2"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">{isRTL ? 'آمن 100%' : '100% Secure'}</div>
                  <div className="text-xs text-slate-500">{isRTL ? 'مشفر' : 'Encrypted'}</div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute -end-8 bottom-1/4 bg-white rounded-2xl p-3 shadow-lg flex items-center gap-2"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-900">{isRTL ? 'سريع' : 'Fast'}</div>
                  <div className="text-xs text-slate-500">{isRTL ? 'API فوري' : 'Instant API'}</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
